// ─────────────────────────────────────────────────────────────────────────────
// THE DATABASE CLIENT
//
// Works against Supabase or against our own PostgREST on Azure, decided by
// which environment variables are set. Both are the same API — PostgREST is
// what Supabase runs underneath — so the client library is unchanged and only
// the address and the token differ.
//
// SUPABASE      url + anon key. The anon key is the token; supabase-js sends it
//               as both apikey and Authorization, and a signed-in user's JWT
//               replaces the latter.
//
// OUR OWN       url + an Entra access token per request. There is no anon key
//               and no apikey header — PostgREST on Azure does not know what
//               one is. What it wants is Authorization: Bearer <entra token>,
//               and the role inside that token is what decides access.
//
// WHY THE TOKEN IS FETCHED PER REQUEST rather than held: an Entra access token
// lasts about an hour. Captured once at startup, Core would work for an hour
// and then refuse everything until the page was reloaded — which presents as
// the system randomly logging people out mid-afternoon.
// ─────────────────────────────────────────────────────────────────────────────

import { createClient } from "@supabase/supabase-js";

const url = process.env.REACT_APP_SUPABASE_URL;
const key = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Entra is in use when its three settings are present. The anon key becoming
// unnecessary is how we know which backend we are on.
const entraClientId = process.env.REACT_APP_ENTRA_CLIENT_ID;
const entraTenantId = process.env.REACT_APP_ENTRA_TENANT_ID;
const entraScope    = process.env.REACT_APP_ENTRA_API_SCOPE;
export const usingEntra = Boolean(entraClientId && entraTenantId && entraScope);

export const isConfigured = Boolean(url && (key || usingEntra));

// Set by the auth module once somebody is signed in. A function rather than a
// value, so every request asks for a current token instead of reusing one that
// may have expired twenty minutes ago.
let tokenProvider = null;
export function setTokenProvider(fn) {
  tokenProvider = fn;
}

export const supabase = !isConfigured
  ? null
  : usingEntra
    ? createClient(url, "unused-but-required-by-the-client", {
        auth: { persistSession: false, autoRefreshToken: false },
        global: {
          // Called for every request. supabase-js insists on a key argument
          // above; PostgREST on Azure ignores apikey entirely and reads only
          // the Authorization header, which this replaces.
          fetch: async (input, init = {}) => {
            const headers = new Headers(init.headers || {});
            headers.delete("apikey");
            const token = tokenProvider ? await tokenProvider() : null;
            if (token) headers.set("Authorization", `Bearer ${token}`);
            else headers.delete("Authorization");

            // STRIP /rest/v1. supabase-js prefixes every request with it
            // because Supabase puts a gateway in front of PostgREST that
            // routes on that path and removes it. Plain PostgREST has no
            // gateway: it sees /rest/v1/rpc/ea_entities_list and looks for a
            // function called "v1", which does not exist — so every call
            // returns 404 and the screen shows "no records returned" as though
            // the database were empty.
            //
            // Nothing in the application changes. The client library still
            // builds its URLs the way it always has; this removes the prefix
            // on the way out.
            const url = typeof input === "string" ? input : input.url;
            const fixed = url.replace("/rest/v1/", "/");
            const target = typeof input === "string"
              ? fixed
              : new Request(fixed, input);

            return fetch(target, { ...init, headers });
          },
        },
      })
    : createClient(url, key);
